import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useEventListener, useInterval } from 'usehooks-ts';
import { isLivePreviewEmbedded } from './previewHelper';
import { useLivePreviewStore } from '../stores/index.js';

export interface PreviewHandlerMap {
    [messageType: string]: ((payload: any, event: MessageEvent) => void) | undefined;
}

export interface UsePreviewReceiverOptions {
    /**
     * Whether the receiver is active (defaults to checking if embedded/preview)
     */
    enabled?: boolean;
    /**
     * Map of message type to handler function
     */
    handlers?: PreviewHandlerMap;
    /**
     * Optional callback when connection is confirmed (READY or ACK/PONG)
     */
    onConnect?: () => void;
}

/**
 * Unified hook for receiving live preview updates and managing the postMessage handshake with Bigfoot Dashboard.
 * Handles:
 * - Announcing readiness (BIGFOOT_PREVIEW_READY) to parent iframe and opener window
 * - Responding to keep-alive pings (BIGFOOT_PREVIEW_PING -> BIGFOOT_PREVIEW_PONG)
 * - Dispatching payload to registered message handlers
 * - Acknowledging received updates (BIGFOOT_PREVIEW_ACK)
 */
export const usePreviewReceiver = ({ enabled, handlers = {}, onConnect }: UsePreviewReceiverOptions = {}) => {
    const location = useLocation();
    const isEmbedded = isLivePreviewEmbedded(location);
    const isActive = enabled !== undefined ? enabled : isEmbedded;

    const [isConnected, setIsConnected] = useState(false);

    const handleMessage = useCallback(
        (event: MessageEvent) => {
            const data = event.data;
            if (!data || typeof data !== 'object') return;

            const targetWindow =
                event.source || (window.opener && !window.opener.closed ? window.opener : window.parent);

            // Any valid Bigfoot message from parent/opener confirms the live bridge connection
            if (typeof data.type === 'string' && data.type.startsWith('BIGFOOT_')) {
                setIsConnected(true);
                useLivePreviewStore.getState().setPreviewConnected(true);
            }

            if (data.type === 'BIGFOOT_PREVIEW_PING') {
                if (targetWindow && targetWindow !== window) {
                    targetWindow.postMessage({ type: 'BIGFOOT_PREVIEW_PONG', timestamp: Date.now() }, '*');
                }
                onConnect?.();
                return;
            }

            const handler = handlers[data.type];
            if (handler && data.payload !== undefined) {
                handler(data.payload, event);
                onConnect?.();
                if (targetWindow && targetWindow !== window) {
                    targetWindow.postMessage({ type: 'BIGFOOT_PREVIEW_ACK', timestamp: Date.now() }, '*');
                }
            }
        },
        [handlers, onConnect]
    );

    const handleMessageWrapper = useCallback(
        (event: MessageEvent) => {
            if (isActive) {
                handleMessage(event);
            }
        },
        [isActive, handleMessage]
    );

    useEventListener('message', handleMessageWrapper);

    const announceReady = useCallback(() => {
        if (!isActive) return;
        if (window.parent && window.parent !== window) {
            window.parent.postMessage({ type: 'BIGFOOT_PREVIEW_READY', version: 1 }, '*');
        }
        if (window.opener && !window.opener.closed && window.opener !== window) {
            window.opener.postMessage({ type: 'BIGFOOT_PREVIEW_READY', version: 1 }, '*');
        }
    }, [isActive]);

    useEffect(() => {
        if (isActive) {
            announceReady();
        }
    }, [isActive, announceReady]);

    // Send ready announcements until bridge connection is established
    useInterval(announceReady, isActive && !isConnected ? 1000 : null);

    return {
        isConnected,
        isEmbedded,
        isActive,
    };
};

export default usePreviewReceiver;
